<div class="header">
  <div class="content" [ngsRevealSet]="{ reset:false, scale:1, distance: '20px'}" [ngsInterval]="50" [ngsSelector]="'.pageTitle'">
    <div class="contentInner">
      <div class="pageTitle">
        <h1 class="headline">Dash Enterprise</h1>
      </div>
    </div>
    <app-enterprise-tab-navigator></app-enterprise-tab-navigator>
  </div>
</div>

<div class="content">
  <div class="contentInner">
    <h1 class="title">Management Dashboard</h1>
    <p class="intro">The Dash Enterprise management dashboard allows you to view and manage all of your Dash display
      devices, and is available to all Dash Enterprise customers.  Please contact
      <a href="mailto:dash@get-dash.com">Dash support</a> for an account.</p>

    <p>This document describes the main sections of the dashboard.  Familiarize yourself with the dashboard layout and
      its screens, and then read our documentation on <a routerLink="../dashboard-configuration">configuring Dash</a>.
    </p>

    <h1 class="sectionHeader">Account overview</h1>
    <app-illustration src="/assets/images/dash-enterprise-account-overview.png" webpSrc="/assets/images/dash-enterprise-account-overview.webp" width="1077" height="693" class="illustration"></app-illustration>
    <p>Click on the user avatar at the top right of the screen to open the account overview.  Here you can see your account
    name and email address, activate or deactivate two-factor authentication, and log out of your account.</p>
    <div style="clear:both"></div>

    <h1 class="sectionHeader">Activating two-factor authentication</h1>
    <p>If your account does not already have two-factor authentication enabled, you will see an option to enable it in the
      account overview menu.  To activate two-factor authentication on your account, follow these steps:</p>
    <ol>
      <li>Click "Set up 2fa" and then click "Set up" on the information dialog that appears.  You will be asked to sign in
        to your account again.</li>
      <li>You'll be shown a QR code that you need to scan in to a TOTP-compatible authenticator app on your mobile phone.
        To continue, open the app on your phone, click "Add" and then scan the code with your phone's camera.  If you
        are not able to scan the QR code, click "Enter a code instead" and enter the code shown into your app.
        If asked, enter the following settings:
      <ul>
        <li>Time-based</li>
        <li>Refresh time: 30 seconds</li>
        <li>Digits: 6</li>
        <li>Algorithm: SHA-1 or HMAC-SHA1</li>
      </ul>
      </li>
      <li>Click "Next" then enter an optional name for your device (this name will be shown to you when you sign in), and enter a code from your authenticator app to test that the setup was successful.</li>
      <li>Click "Check code".  If the setup was successful, two-factor authenticaiton will be set up on your account and you'll need to enter a code from your authenticator app each time you sign in.</li>
    </ol>
    <div class="mfa-illustration-container">
      <app-illustration src="/assets/images/dash-enterprise-2fa-overview.png" webpSrc="/assets/images/dash-enterprise-2fa-overview.webp" width="1077" height="693" class="mfa-illustration"></app-illustration>
      <app-illustration src="/assets/images/dash-enterprise-2fa-code-overview.png" webpSrc="/assets/images/dash-enterprise-2fa-code-overview.webp" width="1077" height="693" class="mfa-illustration"></app-illustration>
      <app-illustration src="/assets/images/dash-enterprise-2fa-validation.png" webpSrc="/assets/images/dash-enterprise-2fa-validation.webp" width="1077" height="693" class="mfa-illustration"></app-illustration>
    </div>

    <h1 class="sectionHeader">Deactivating two-factor authentication</h1>
    <p>If you have two-factor authentication enabled on your account, you can disable it by clicking "Turn off 2fa" in
      the account overview menu.  You'll be asked to sign in again, after which 2fa will be turned off on your account.
      Note that if an administrator has set your account to require 2fa, you won't see the option to turn it off.</p>

    <p class="tip icon">
      <span>If you've lost your anthenticator device, you can ask an administrator to turn off 2fa on your account, or
        contact Dash Support for assistance.  Once you've signed in to your account, you can set up 2fa again on a new device.</span>
    </p>

    <h1 class="sectionHeader">Main screen and Organization overview</h1>
    <app-illustration src="/assets/images/dash-enterprise-org-overview.png" webpSrc="/assets/images/dash-enterprise-org-overview.webp" width="1077" height="693" class="illustration"></app-illustration>
    <p>The main Dash screen has three key areas: a title bar at the top, a navigation bar on the left, and a central
      content section. On smaller devices, the navigation bar can be collapsed — just click the double arrow at the top to
      expand or collapse it.  To log out, click the user icon in the top right corner and select <i>Log out</i>.</p>

    <p>You can move around Dash Enterprise by clicking sections in the left-hand navigation bar. Once you're in a section,
      use the breadcrumb trail at the top of the screen to navigate back.</p>

    <p>The <a href="https://enterprise.get-dash.com/" target="_blank">Organization overview</a> gives a snapshot of your
      organization’s current setup and Dash Enterprise subscription. It shows how many devices are included in your
      license, how many are currently active, and your subscription renewal date.  You can also click the shortcut
      buttons to manage your subscription and devices.</p>

    <h1 class="sectionHeader">All devices</h1>
    <app-illustration src="/assets/images/dash-enterprise-devices-overview.png" webpSrc="/assets/images/dash-enterprise-devices-overview.webp"  width="1483" height="888" class="illustration"></app-illustration>
    <p>The <a href="https://enterprise.get-dash.com/devices" target="_blank">Devices overview</a> shows all of the
      devices currently registered to your organization.   lists all the devices registered to your organization. You’ll
      see details like the device name, IP address, the meeting room calendar it’s showing, when it was added, its
      <a href="">group</a> (if it belongs to one), and the number of configuration options set on the device.</p>

    <p>You can customize the table by dragging columns to re-order them, sorting by clicking any column header, or
      filtering the list by typing in the search box above the table.</p>

    <p class="tip icon single-line">Each device has a colored status marker to the left of its name.  The marker is green if the device has recently
      checked in to the dashboard, and red if it has been disconnected.
    </p>

    <h3>Adding devices</h3>
    <p>
      To add a device directly to your organization, activate it by entering your license key in Dash's settings.  Open
      Dash on your device, and tap the cog icon to open <i>Settings</i>, open the <i>Subscription</i> section, and tap
      <i>Enterprise license</i>.  Enter your license key to activate the device.  Your device will appear in the devices
      overview table, without an assigned group.  To add a device directly to a group, you can activate it using a
      pairing code (please see the groups section below).
    </p>

    <h3>Deactivating devices</h3>
    <p>To deactivate unused devices, select the devices that you would like to deactivate (by using the checkboxes on
      the left side of the table, or using <i>shift</i> and <i>ctrl</i> to select the rows in the table), and then press
      <i>Deactivate</i> in the toolbar.</p>
    <p class="tip icon">
      <span>Deactivating devices from the console is most useful to remove old devices from your organization.  If your device
      is still running Dash, it will re-activate itself the next time it checks in with our license server (which happens
      approximately once per day).</span> <br><br> <span>To permanently deactivate running devices, we recommend using the <i>Deactivate</i>
      button on the device itself (which you can find in the subscription section of Dash's settings).</span>
    </p>

    <h3>Assigning devices to groups</h3>
    <app-illustration src="/assets/images/dash-enterprise-set-group.png" webpSrc="/assets/images/dash-enterprise-set-group.webp" width="600" height="266" class="illustration"></app-illustration>
    <p>
      To assign devices to groups, select the devices that you would like to assign (by using the checkboxes on
      the left side of the table, or using <i>shift</i> and <i>ctrl</i> to select the rows in the table), and then press
      <i>Set group</i> in the toolbar.  Select the group that you would like to assign the selected devices to from
      the dropdown.
    </p>
    <p>
      For more information about device groups and how you can use them to automatically configure your Dash devices,
      please see the Device Groups section of this document below.
    </p>

    <h1 class="sectionHeader" id="groups">Device groups</h1>
    <p>
      Device groups allow you to configure your Dash devices through the management dashboard.  Each group can be set with
      configuration options that will be applied to all devices in that group.  This means that a device group can be
      treated as a collection of Dash devices that you would like to configure in the same way.  For simple setups
      (where you would like all your Dash devices to be configured in the same way), you can have a single group for all
      your devices.  For more complex setups, you could have one group for each office location, or for each type of
      meeting room.
    </p>
    <p class="tip icon">Any settings not set in the group can still be set locally on each device, so for example you can set custom
      branding and optional features using the device group, and choose the room calendar to display on the device itself.</p>

    <h3>Device groups overview</h3>
    <app-illustration src="/assets/images/dash-enterprise-groups-overview.png" webpSrc="/assets/images/dash-enterprise-groups-overview.webp" width="1483" height="888" class="illustration"></app-illustration>
    <p>
      The device groups overview shows your current device groups.  For each group you can see the number of devices
      assigned to it, and also the number of configuration options it contains.  You can filter the displayed groups
      using the filter box in the top right hand corner, and click on a group to open it and see its details.
    </p>
    <p>To create a new device group, click the <i>Create group</i> button in the toolbar, and enter a name for your group.</p>
    <p>To delete a group, click the trash icon in the top right hand corner of the group.  Any devices currently assigned
      to the group will remain active, but will be unassigned from the group.</p>
    <div style="clear:both;margin-bottom: 20px;"></div>
    <p class="tip icon single-line" style="clear:both"><span>To learn how to use device groups to configure Dash, please see our section on <a routerLink="../dashboard-configuration">configuring Dash</a></span></p>
  </div>
</div>
